import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { useLang } from "@/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1549893072-4bc678117f45?auto=format&fit=crop&w=2000&q=80";

const Line = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-2 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();
  const lenis = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (id) => () => lenis?.scrollTo(`#${id}`, { offset: -64, duration: 1.5 });

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-navy text-ivory">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Portofino, Golfo del Tigullio" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/75 via-navy/55 to-navy" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1400px] flex-col justify-end px-6 pb-16 pt-36 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mb-8 flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.4em] text-champagne"
        >
          <span className="h-px w-12 bg-champagne/70" />
          {t.hero.overline}
        </motion.p>

        <h1 data-testid="hero-title" className="max-w-5xl font-serif text-5xl font-light leading-[1.04] tracking-tight sm:text-6xl lg:text-8xl">
          <Line delay={0.35}>{t.hero.lines[0]}</Line>
          <Line delay={0.5} className="italic text-champagne">{t.hero.lines[1]}</Line>
          <Line delay={0.65}>{t.hero.lines[2]}</Line>
        </h1>

        <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl text-base font-light leading-relaxed text-ivory/75 md:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={go("contatti")}
              data-testid="hero-cta-contact"
              className="group flex items-center gap-3 bg-champagne px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-ivory"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={go("servizi")}
              data-testid="hero-cta-services"
              className="border border-ivory/40 px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:border-champagne hover:text-champagne"
            >
              {t.hero.ctaSecondary}
            </motion.button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.3 }}
          className="mt-16 grid grid-cols-1 gap-8 border-t border-ivory/15 pt-10 sm:grid-cols-2"
          data-testid="hero-stats"
        >
          {(t.hero.stats || []).map((s) => (
            <div key={s.label} className="flex items-baseline gap-4">
              <span className="font-serif text-4xl font-light text-champagne lg:text-5xl">{s.value}</span>
              <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-ivory/60">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 lg:block"
      >
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
          <ArrowDown size={22} weight="light" className="text-ivory/50" />
        </motion.div>
      </motion.div>
    </section>
  );
}
